<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useDisplay } from "vuetify";
import Logo from "@/components/global/Logo";
import { useRoute, useRouter } from "vue-router";
import { useUserStore, useGlobalStore } from "@/stores";

const props = defineProps({
  modelValue: { type: Boolean, default: true },
});
const emit = defineEmits(["update:modelValue"]);

const { mobile } = useDisplay();

const drawerOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

const route = useRoute();
const router = useRouter();

watch(route, () => {
  if (mobile.value) emit("update:modelValue", false);
});

const userStore = useUserStore();

const globalStore = useGlobalStore();

const user = userStore.user;

const notifications = ref([]);

const userImage = computed(() => {
  return user.imageLink ? user.imageLink : "";
});

function handleLogout() {
  userStore.logout();
}

function handleWhatsNew() {
  router.push({
    path: `/notifications/whats-new`,
  });
}

function goToSettings() {
  router.push({
    path: `/user-settings`,
  });
}

const menuColor = computed(() => {
  const isAcademy = route.matched.some((m) => {
    return m.path === "/academy";
  });

  const isCompany = route.matched.some((m) => {
    return m.path === "/company" || m.path === "/company/job-position";
  });

  const isEmployability = route.matched.some((m) => {
    return m.path === "/employability";
  });

  const isLivelihood = route.matched.some((m) => {
    return m.path === "/livelihood";
  });

  const isAdmin = route.matched.some((m) => {
    return m.path === "/admin";
  });

  return isAcademy
    ? "academy"
    : isCompany
    ? "company"
    : isEmployability || isLivelihood
    ? "employability"
    : isAdmin
    ? "admin"
    : "primary";
});

var links = ref([
  {
    text: "Home",
    icon: "mdi-home",
    link: "/dashboard",
  },
  {
    text: "Beneficiaries",
    icon: "mdi-account-star",
    children: [
      {
        text: "Overview",
        icon: "mdi-table",
        link: "/beneficiary/intake/overview",
        disabled: false,
      },
      {
        text: "Dashboard",
        icon: "mdi-chart-pie",
        link: "/beneficiary/dashboard",
        disabled: false,
      },
      {
        text: "Projects Reports",
        icon: "mdi-chart-arc",
        link: "/project/reports",
        disabled: false,
      },
      {
        text: "New Intake",
        icon: "mdi-account-plus",
        link: "/beneficiary/intake/create",
        disabled: false,
      },
    ],
  },
  {
    text: "Academy",
    icon: "mdi-school",
    link: "/academy",
    children: [
      {
        text: "Overview",
        icon: "mdi-table",
        link: "/academy/overview",
        disabled: false,
      },
      {
        text: "Dashboard",
        icon: "mdi-chart-pie",
        link: "/academy/dashboard",
        disabled: false,
      },
      {
        text: "Evaluations Dashboard",
        icon: "mdi-chart-pie",
        link: "/academy/evaluations/dashboard",
        disabled: false,
      },
      {
        text: "Add a Course",
        icon: "mdi-pen-plus",
        link: "/academy/add",
        disabled: false,
      },
      {
        text: "Add Student Evaluation",
        icon: "mdi-file-document-plus-outline",
        link: "/academy/evaluation/add-student",
        disabled: false,
      },
      {
        text: "Add Course Evaluation",
        icon: "mdi-file-document-plus-outline",
        link: "/academy/evaluation/add-course",
        disabled: false,
      },
    ],
  },
  {
    text: "Employability",
    icon: "mdi-briefcase",
    link: "/employability",
    children: [
      {
        text: "Employability Overview",
        icon: "mdi-table",
        link: "/employability/overview",
        disabled: false,
      },
      // {
      //   text: "Service Overview",
      //   icon: "mdi-table-large",
      //   link: "/employability/service-overview",
      //   disabled: false,
      // },
      // {
      //   text: "Livelihood Overview",
      //   icon: "mdi-table",
      //   link: "/livelihood/overview",
      //   disabled: false,
      // },
      {
        text: "Dashboard",
        icon: "mdi-chart-pie",
        link: "/employability/dashboard",
        disabled: false,
      },
      {
        text: "Add Employability Service",
        icon: "mdi-briefcase-plus",
        link: "/employability/add",
        disabled: false,
      },
      // {
      //   text: "Add Livelihood",
      //   icon: "mdi-file-document-plus-outline",
      //   link: "/livelihood/add",
      //   disabled: false,
      // },
      {
        text: "Add Livelihood",
        icon: "mdi-file-document-plus-outline",
        link: "/livelihood/questionnaire",
        disabled: false,
      },
      {
        text: "Livelihood Overview",
        icon: "mdi-table",
        link: "/livelihood/questionnaire/overview",
        disabled: false,
      },
    ],
  },
  {
    text: "Companies",
    icon: "mdi-domain",
    link: "/company",
    children: [
      {
        text: "Companies Overview",
        icon: "mdi-table",
        link: "/company/overview",
        disabled: false,
      },
      {
        text: "Job Positions Overview",
        icon: "mdi-briefcase",
        link: "/company/job-position/overview",
        disabled: false,
      },
      {
        text: "Add Company",
        icon: "mdi-domain-plus",
        link: "/company/add",
        disabled: false,
      },
      {
        text: "Add Job Position",
        icon: "mdi-briefcase-plus",
        link: "/company/job-position/add",
        disabled: false,
      },
    ],
  },
]);

onMounted(async () => {
  const roles = JSON.parse(localStorage.getItem("roles") || "[]");
  const isAdmin = roles.some((role) => role.name == "ROLE_ADMIN");
  if (isAdmin) {
    links.value.push({
      text: "Admin",
      icon: "mdi-shield-crown",
      link: "/admin",
      children: [
        {
          text: "User Overview",
          icon: "mdi-table",
          link: "/admin/user/overview",
          disabled: false,
        },
        {
          text: "Add User",
          icon: "mdi-domain-plus",
          link: "/admin/user/add",
          disabled: false,
        },
        {
          text: "Edit Lists",
          icon: "mdi-list-box-outline",
          link: "/admin/edit/lists",
          disabled: false,
        },
      ],
    });
  }
  notifications.value = await globalStore.getWhatsNewNotifications();
});
</script>

<template>
  <v-navigation-drawer
      v-model="drawerOpen"
      :color="menuColor"
      class="menu-drawer"
      :permanent="!mobile"
      :temporary="mobile"
      :scrim="false"
      mobile-breakpoint="0"
    >
      <div>
        <div class="pr-5 pl-5 pt-2 mb-2">
          <Logo :color="menuColor" />
        </div>
        <div v-if="user" class="pa-5 d-flex align-center">
          <v-avatar color="avatar" size="50" :class="`bg-avatar-${menuColor}`">
            <v-img alt="User Image" :src="userImage" />
          </v-avatar>
          <div class="name-holder ml-5">
            <h3>{{ user.name }}</h3>
            <div
              class="text-sm-caption d-flex cursor-pointer"
              @click="goToSettings"
            >
              <v-icon class="mr-1 pt-1" size="small"> mdi-cog </v-icon>
              Account Settings
            </div>
          </div>
        </div>
        <v-divider :thickness="4" />
        <v-list nav open-strategy="single">
          <template v-for="item in links" :key="item.text">
            <v-list-group
              v-if="item.children"
              :value="item"
              :to="{ path: item.link }"
            >
              <template #activator="{ props }">
                <v-list-item v-bind="props" :title="item.text">
                  <template #prepend>
                    <v-icon :icon="item.icon" />
                  </template>
                </v-list-item>
              </template>

              <v-list-item
                v-for="child in item.children"
                :key="child.name"
                :value="child"
                :to="{ path: child.link }"
                :disabled="child.disabled"
              >
                <template #prepend>
                  <v-icon :icon="child.icon" />
                </template>

                <v-list-item-title v-text="child.text" />
              </v-list-item>
            </v-list-group>
            <v-list-item v-else :value="item" :to="{ path: item.link }">
              <template #prepend>
                <v-icon :icon="item.icon" />
              </template>

              <v-list-item-title v-text="item.text" />
            </v-list-item>
          </template>
        </v-list>
      </div>
      <template #append>
        <div class="pa-2 d-flex justify-center">
          <v-badge
            v-if="notifications.length > 0"
            right
            color="error"
            :content="notifications.length"
          >
            <v-btn variant="text" @click="handleWhatsNew">
              <v-icon icon="mdi-bell" class="pr-2" /> What's New
            </v-btn>
          </v-badge>

          <v-btn v-else variant="text" disabled>
            <v-icon icon="mdi-bell" class="pr-2" /> What's New
          </v-btn>
        </div>
        <div class="pa-2 d-flex justify-center">
          <v-btn variant="text" @click="handleLogout">
            <v-icon icon="mdi-logout" class="pr-2" /> logout
          </v-btn>
        </div>
      </template>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
.bg-avatar {
  background-image: url("../../assets/avatar.png");
  background-size: cover;
  background-color: white;
}
.bg-avatar.bg-avatar-academy {
  background-image: url("../../assets/avatar_academy.png") !important;
}
.bg-avatar.bg-avatar-employability {
  background-image: url("../../assets/avatar_employability.png");
  background-color: rgba($color: #000000, $alpha: 0.75);
}
.bg-avatar.bg-avatar-company {
  background-image: url("../../assets/avatar_company.png");
  background-color: rgba($color: #000000, $alpha: 0.75);
}
.v-list-group__items {
  .v-list-item {
    padding-left: 20px !important;
  }
}
</style>
